import { Icon, ThemeSwitch } from '@wtfalch/design';

import type { Agora } from '../agoras.ts';
import { notePickedTheme } from '../design/bootTheme.ts';
import { product } from '../design/product.ts';
import type { HoursClient } from '../hours/client.ts';
import { WorkingHoursForm } from '../hours/WorkingHoursForm.tsx';
import { pathForAgora } from '../routes.ts';
import './settings.css';

/**
 * A person's own preferences, not a company's -- reached from the account
 * menu rather than the place rail: `AgoraShell`'s rail is the four things a
 * company does, and this isn't one, so it carries none of that chrome, the
 * same as `/add` and `/` (agora#258).
 *
 * **Appearance needs no store.** `ThemeSwitch` keeps the choice in
 * `localStorage` under one key across the estate -- a fact about this screen,
 * not about the person: the same account on a laptop in a bright room and a
 * phone at night wants two different answers, and no colleague ever needs to
 * read it.
 *
 * **Working hours do need one.** A colleague's client has to be able to read
 * them, so the section is shown only when the company has an hours service
 * (`hours`, `apps/hr`) and saves through it. The current status, the rest of
 * the issue's list, still waits on a store decision; add nothing else here
 * unless it shares Appearance's "no store needed" property or goes through
 * that service.
 */
export function Settings({
  agora,
  go,
  hours,
}: {
  agora: Agora;
  go: (path: string) => void;
  /** The working-hours service, when this company has one (agora#258). */
  hours?: HoursClient;
}) {
  return (
    <div className="settings-page">
      <header className="settings-header">
        <button type="button" className="settings-back" onClick={() => go(pathForAgora(agora.id))}>
          <Icon name="chev-left" size={16} />
          {agora.name}
        </button>
        <h1 className="settings-title">Settings</h1>
      </header>
      <section className="settings-section">
        <h2 className="settings-section-title">Appearance</h2>
        <ThemeSwitch product={product} labelHidden onChange={notePickedTheme} />
      </section>
      {hours && (
        <section className="settings-section">
          <h2 className="settings-section-title">Working hours</h2>
          <p className="settings-section-note">
            Your colleagues see these, so they know when you are likely to answer.
          </p>
          <WorkingHoursForm client={hours} />
        </section>
      )}
    </div>
  );
}
